import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { IAction } from '@/modules/gpt-chats/types/tools.service.type';
import { useTranslation } from 'react-i18next';

interface ActionDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: IAction | null;
  isBlocksTool?: boolean;
}

const formatDefaultValue = (value: any) => {
  if (value && typeof value === 'object') {
    return JSON.stringify(value);
  }
  return value || 'True';
};

export const ActionDetailsModal = ({ open, onOpenChange, action }: ActionDetailsModalProps) => {
  const { t } = useTranslation();
  if (!action) return null;

  const inputParameters = Object.entries(action.InputParameters || {});
  const outputParameters = Object.entries(action.OutputParameters || {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{action.Name}</DialogTitle>
          <p className="text-sm text-muted-foreground">{action.Description}</p>
        </DialogHeader>

        <Tabs defaultValue="input" className="mt-4 flex flex-1 flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="input">{t('INPUT_PARAMS')}</TabsTrigger>
            <TabsTrigger value="output">{t('OUTPUT_PARAMS')}</TabsTrigger>
          </TabsList>

          <TabsContent value="input" className="mt-4 flex-1 overflow-y-auto">
            {inputParameters.length > 0 ? (
              <Table className="text-sm">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-medium text-muted-foreground">
                      {t('FIELD')}
                    </TableHead>
                    <TableHead className="font-medium text-muted-foreground">
                      {t('DESCRIPTION')}
                    </TableHead>
                    <TableHead className="font-medium text-muted-foreground">
                      {t('PARAM_TYPE')}
                    </TableHead>
                    <TableHead className="font-medium text-muted-foreground">
                      {t('REQUIRED')}
                    </TableHead>
                    <TableHead className="font-medium text-muted-foreground">
                      {t('DEFAULT_VALUE')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inputParameters.map(([key, param]) => (
                    <TableRow key={key}>
                      <TableCell className="font-medium">{param.Name}</TableCell>
                      <TableCell className="text-muted-foreground">{param.Description}</TableCell>
                      <TableCell className="text-muted-foreground">{param.Type}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {param.Required ? 'YES' : 'No'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDefaultValue(param.DefaultValue)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex h-32 items-center justify-center text-center text-muted-foreground">
                {t('NO_DATA')}
              </div>
            )}
          </TabsContent>

          <TabsContent value="output" className="mt-4 flex-1 overflow-y-auto">
            {outputParameters.length > 0 ? (
              <Table className="text-sm">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-medium text-muted-foreground">
                      {t('FIELD')}
                    </TableHead>
                    <TableHead className="font-medium text-muted-foreground">
                      {t('DESCRIPTION')}
                    </TableHead>
                    <TableHead className="font-medium text-muted-foreground">
                      {t('PARAM_TYPE')}
                    </TableHead>
                    <TableHead className="font-medium text-muted-foreground">
                      {t('REQUIRED')}
                    </TableHead>
                    <TableHead className="font-medium text-muted-foreground">
                      {t('DEFAULT_VALUE')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outputParameters.map(([key, param]) => (
                    <TableRow key={key}>
                      <TableCell className="font-medium">{param.Name}</TableCell>
                      <TableCell className="text-muted-foreground">{param.Description}</TableCell>
                      <TableCell className="text-muted-foreground">{param.Type}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {param.Required ? 'YES' : 'No'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDefaultValue(param.DefaultValue)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex h-32 items-center justify-center text-center text-muted-foreground">
                {t('NO_DATA')}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
